import React from 'react';
import {
  GeolocationLocationSetData,
  IGeolocationSet,
} from '../../../../../types';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Box,
  IconButton,
  CardActions,
  Slider,
} from '@mui/material';
import { buildAddress } from '../../../../../utils/helpers/DiyHelpers';
import { PrimaryButton } from '../../../../Buttons';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {
  constantStringToHumanized,
  humanizeString,
} from '../../../../../utils/stringModifier';
import { getLocationFieldName } from '../../../../../utils/helpers/AssetHelpers';

interface GeolocationListProps {
  geolocation: GeolocationLocationSetData;
  search?: boolean;
  exclude?: boolean;
  formValues?: IGeolocationSet;
  onChangeRadius?: (radius: string, value: GeolocationLocationSetData) => void;
  onRemove?: (value: GeolocationLocationSetData, exclude: boolean) => void;
  onSelectLocation?: (
    value: GeolocationLocationSetData,
    exclude: boolean,
  ) => void;
  withAction?: boolean;
}

const GeolocationList: React.FC<GeolocationListProps> = ({
  geolocation,
  search = false,
  exclude = false,
  formValues,
  onChangeRadius,
  onRemove,
  onSelectLocation,
  withAction = false,
}) => {
  const isCustomLocation = !geolocation.type;

  const included = formValues?.geolocations[
    getLocationFieldName(geolocation.type)
  ]?.filter(
    (includedLocation: GeolocationLocationSetData) =>
      includedLocation.key === geolocation.key,
  );
  const excluded = formValues?.excludedGeolocations[
    getLocationFieldName(geolocation.type)
  ]?.filter(
    (includedLocation: GeolocationLocationSetData) =>
      includedLocation.key === geolocation.key,
  );

  const humanizeLocationType = (type: string) => {
    if (!type) return 'Address';

    if (type === 'geo_market') return 'DMA';

    if (type === 'medium_geo_area') return 'County';

    if (type === 'region') return 'State';

    return constantStringToHumanized(type);
  };

  return (
    <Card>
      <CardContent sx={{ py: '5px !important' }}>
        <Grid
          container
          spacing={2}
          sx={{ alignItems: 'center', display: 'flex' }}
        >
          <Grid
            item
            xs={12}
            sm={withAction ? 6 : 9}
            sx={{ flexDirection: 'column' }}
          >
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              {isCustomLocation
                ? humanizeString(geolocation.address_string)
                : geolocation.name}
            </Typography>

            <Typography variant="body2">
              {isCustomLocation
                ? `Lat: ${geolocation.latitude.toFixed(
                    5,
                  )}, Lng: ${geolocation.longitude.toFixed(5)}`
                : buildAddress(geolocation)}
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6} md={3} sx={{ flexDirection: 'column' }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              Type
            </Typography>

            <Typography variant="body2">
              {humanizeLocationType(geolocation.type)}
            </Typography>
          </Grid>

          {withAction ? (
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              sx={{
                flexDirection: 'column',
                justifyContent: 'center',
                display: 'flex',
              }}
            >
              {search ? (
                <>
                  {!!excluded?.length ? null : (
                    <Box component="div">
                      <PrimaryButton
                        title={!!included?.length ? 'Included' : 'Include'}
                        type="button"
                        variant="text"
                        handleOnClick={() =>
                          onSelectLocation(geolocation, exclude)
                        }
                        disabled={!!included?.length}
                      />
                    </Box>
                  )}

                  {!!included?.length ? null : (
                    <Box component="div">
                      <PrimaryButton
                        title={!!excluded?.length ? 'Excluded' : 'Exclude'}
                        type="button"
                        variant="text"
                        handleOnClick={() =>
                          onSelectLocation(geolocation, true)
                        }
                        disabled={!!excluded?.length}
                        theme="red"
                      />
                    </Box>
                  )}
                </>
              ) : (
                <IconButton onClick={() => onRemove(geolocation, exclude)}>
                  <DeleteOutlineOutlinedIcon sx={{ color: 'red' }} />
                </IconButton>
              )}
            </Grid>
          ) : null}
        </Grid>
      </CardContent>

      {!search &&
      (geolocation.type === 'city' || !geolocation.type) &&
      !exclude ? (
        <CardActions
          sx={{
            paddingTop: '5px',
            flexDirection: 'column',
            paddingX: '20px',
          }}
        >
          <Typography variant="caption">{`${
            geolocation?.radius || 50
          } miles`}</Typography>

          {withAction ? (
            <Slider
              defaultValue={50}
              min={10}
              max={50}
              valueLabelDisplay="auto"
              value={geolocation?.radius || 50}
              onChange={(e: any) => onChangeRadius(e.target.value, geolocation)}
              sx={{ width: '300px' }}
            />
          ) : null}
        </CardActions>
      ) : null}
    </Card>
  );
};

export default GeolocationList;
